import type { GridSize } from '@material-ui/core';
import { Box, Grid, InputAdornment, TextField } from '@material-ui/core';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toString from 'lodash/toString';

import { SearchIcon } from '../icon/search_icon/SearchIcon';
import { trackEvent } from '../../utils/analytics';

import { OptionButton } from './OptionButton';

export interface OptionListProps<T> {
  testID?: string;
  options: T[];

  /**
   * The amount of columns to show the options.
   * @default 1
   */
  columns?: number;

  selected?: T | ((option: T) => boolean);
  onSelect?: (option: T) => void;

  isSearchable?: boolean;
  searchPlaceholder?: string;

  /**
   * Convert an option to a string, which is used to be searched by the keyword, and used as the default value of the
   * option key and the option element.
   *
   * @default option.toString()
   */
  optionToString?: (option: T) => string;
  /** Defaults to `optionToString` */
  getOptionKey?: (option: T) => string;
  /** Defaults to `optionToString` */
  renderOption?: (option: T) => ReactNode;

  /**
   * When there are too many options, make the list collapsible. When collapsed, only a few options will be shown
   * with a "Show More" button.
   */
  isCollapsible?: boolean;
}

/** Check if a prop is an option or a factory */
function isCallableProp<T, U>(prop: T | ((option: T) => U)): prop is (option: T) => U {
  return typeof prop === 'function';
}

/** Show this amount of options when the list is collapsed. */
const OPTIONS_TO_SHOW_WHEN_COLLAPSED = 12;

/**
 * A list of options. It supports searching and collapsible.
 */
export function OptionList<T>(props: OptionListProps<T>): JSX.Element {
  const optionToString = props.optionToString || toString;
  const {
    testID,
    options,
    columns = 1,
    selected,
    onSelect,
    isSearchable,
    searchPlaceholder,
    renderOption = optionToString,
    getOptionKey = optionToString,
  } = props;

  const [keyword, setKeyword] = useState('');

  const [keywordEventSent, setKeywordEventSent] = useState(false);
  useEffect(() => {
    if (!keywordEventSent && keyword) {
      void trackEvent('OptionListSearch', {
        eventLabel: testID,
      });
      setKeywordEventSent(true);
    }
  }, [keyword, keywordEventSent, testID]);

  const filteredOptions = useMemo(() => {
    if (!isSearchable && !keyword) {
      return options;
    }
    return options.filter((option) => optionToString(option).toLowerCase().includes(keyword.toLowerCase()));
  }, [isSearchable, keyword, options, optionToString]);

  const isCollapsible = props.isCollapsible && !keyword && filteredOptions.length > OPTIONS_TO_SHOW_WHEN_COLLAPSED;
  const [collapseToggle, setCollapseToggle] = useState(true);
  const isCollapsed = isCollapsible && collapseToggle;

  const [showMoreEventSent, setShowMoreEventSent] = useState(false);
  const onToggleClick = useCallback(() => {
    if (!showMoreEventSent) {
      void trackEvent('OptionListShowMore', {
        eventLabel: testID,
      });
      setShowMoreEventSent(true);
    }
    setCollapseToggle((prev) => !prev);
  }, [showMoreEventSent, testID]);

  return (
    <Box>
      {isSearchable && (
        <Box mb={1}>
          <TextField
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            placeholder={searchPlaceholder}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </Box>
      )}

      <Grid container spacing={1}>
        {filteredOptions
          .slice(0, isCollapsed ? OPTIONS_TO_SHOW_WHEN_COLLAPSED : filteredOptions.length)
          .map((option) => (
            <Grid key={getOptionKey(option)} item xs={(12 / columns) as GridSize}>
              <OptionButton
                selected={isCallableProp(selected) ? selected(option) : selected === option}
                onClick={() => {
                  onSelect?.(option);
                }}
              >
                {renderOption(option)}
              </OptionButton>
            </Grid>
          ))}
      </Grid>

      {isCollapsible && (
        <Box marginTop={1}>
          <OptionButton onClick={onToggleClick}>{isCollapsed ? 'Show More' : 'Show Less'}</OptionButton>
        </Box>
      )}
    </Box>
  );
}
